import React from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";

const TESTIMONIALS = [
  {
    id: 0,
    quote:
      "Banaras trip, 6 of us, zero awkwardness on the return train. Qontri's simplified plan turned 14 transactions into 3.",
    name: "Aanya Mehta",
    role: "Product designer · Mumbai",
    crew: "BANARAS CREW",
    initial: "A",
    bg: "#3056F5",
  },
  {
    id: 1,
    quote:
      "I was The ATM all week in Goa. The role kept popping up in our group chat. Best part — settling was a single QR scan.",
    name: "Rohan Iyer",
    role: "Founder · Bengaluru",
    crew: "GOA TRIP",
    initial: "R",
    bg: "#14254B",
  },
  {
    id: 2,
    quote:
      "Splitwise felt clinical. Qontri feels like a friend. The banter feed is unhinged in the best way possible.",
    name: "Sahil Khanna",
    role: "Software engineer · Delhi",
    crew: "MANALI 2026",
    initial: "S",
    bg: "#0F2C5A",
  },
  {
    id: 3,
    quote:
      "We split rent, groceries and the Sunday brunch with one app. UPI QR with the Qontri brand on it? Underrated detail.",
    name: "Priya Kulkarni",
    role: "PhD student · Pune",
    crew: "FLATMATE FUND",
    initial: "P",
    bg: "#3056F5",
  },
  {
    id: 4,
    quote:
      "Marked Adarsh as The Borrower three trips in a row. He finally settled up. 10/10 social pressure mechanic.",
    name: "Yash Verma",
    role: "Photographer · Jaipur",
    crew: "DESERT RUN",
    initial: "Y",
    bg: "#14254B",
  },
  {
    id: 5,
    quote:
      "Onboarded my parents on Qontri for a family pilgrimage. They get it. That alone deserves a five-star review.",
    name: "Viren Shah",
    role: "Consultant · Ahmedabad",
    crew: "FAMILY YATRA",
    initial: "V",
    bg: "#3056F5",
  },
];

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 bg-[#EDE9FE]/60 backdrop-blur-sm border-y border-[#0F2C5A]/10 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-8"
          >
            <p
              data-testid="testimonials-eyebrow"
              className="text-[#3056F5] font-bold tracking-[0.35em] text-xs mb-5"
            >
              FROM THE CREW
            </p>
            <h2
              data-testid="testimonials-heading"
              className="font-display uppercase text-[#0F2C5A] leading-[0.95] text-[clamp(2.2rem,5.5vw,4.5rem)]"
            >
              Loved by the trips
              <br />
              <span className="text-[#3056F5]">that pay together.</span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="md:col-span-4 flex md:justify-end"
          >
            <div className="inline-flex items-center gap-3 bg-[#F0EAFD] border border-[#0F2C5A]/10 rounded-full px-5 py-3">
              <div className="flex -space-x-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#3056F5] text-[#3056F5]"
                  />
                ))}
              </div>
              <span className="text-[11px] font-bold tracking-[0.2em] text-[#0F2C5A]">
                4.9 · 220+ BETA REVIEWS
              </span>
            </div>
          </motion.div>
        </div>

        <div
          data-testid="testimonials-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {TESTIMONIALS.map((t, i) => (
            <motion.article
              key={t.id}
              data-testid={`testimonial-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
              whileHover={{ y: -6 }}
              className={`relative group rounded-3xl p-7 md:p-8 border border-[#0F2C5A]/10 bg-[#F0EAFD] hover:border-[#0F2C5A]/20 transition-all ${
                i === 1 || i === 4
                  ? "md:translate-y-6"
                  : i === 2
                    ? "md:-translate-y-3"
                    : ""
              }`}
            >
              <div className="flex items-start justify-between mb-6">
                <Quote
                  className="w-8 h-8 text-[#3056F5]"
                  strokeWidth={2}
                  fill="currentColor"
                />
                <span className="text-[10px] font-bold tracking-[0.25em] text-[#3056F5] bg-[#3056F5]/10 px-2.5 py-1 rounded-full">
                  {t.crew}
                </span>
              </div>

              <p className="text-[#0F2C5A] text-base md:text-lg leading-relaxed font-medium">
                &ldquo;{t.quote}&rdquo;
              </p>

              <div className="mt-7 pt-6 border-t border-[#0F2C5A]/10 flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white font-display text-lg"
                  style={{ background: t.bg }}
                >
                  {t.initial}
                </div>
                <div className="leading-tight">
                  <div className="font-display text-[#0F2C5A] text-base">
                    {t.name}
                  </div>
                  <div className="text-[12px] text-[#14254B]/70">{t.role}</div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
